import { Link } from 'react-router-dom';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatDate } from '../../lib/format';
import type { EosSummary } from '../../lib/types';
import type { EosRock, EosTodo } from './eosTypes';
import { ROCK_STATUS_LABELS, eosToday, rockBand } from './eosUi';

type Tab = 'meetings' | 'scorecard' | 'rocks' | 'todos' | 'issues' | 'teams';

/**
 * The module's dashboard — the same five stats and two lists the standalone app
 * opened on.
 *
 * Five, not four, and in that app's order: rocks on track, last week's
 * scorecard, open to-dos, open issues, and the meeting. `.eos-stats` is an
 * auto-fit grid rather than `.grid-4` for exactly that reason — a fifth tile in
 * a four-column grid sits alone on a second row and reads as an afterthought.
 *
 * Underneath, that app's two-column body: this quarter's rocks on the left with
 * a status pill each, the to-dos due soonest on the right.
 */
export function EosOverview({
  summary,
  rocks,
  todos,
  loading,
  error,
  onOpenTab,
}: {
  summary: EosSummary | null;
  rocks: EosRock[];
  todos: EosTodo[];
  loading: boolean;
  error: string | null;
  onOpenTab: (tab: Tab) => void;
}) {
  if (loading) return <Loading label="Loading" />;
  if (error) return <ErrorBanner message={error} />;
  if (!summary) return null;

  /* No team means nobody has set the module up. Said plainly, with the one
     action that fixes it, rather than five zeroes — which would report a
     company in trouble instead of a module that is new. */
  if (summary.teamId === null) {
    return (
      <div className="card">
        <div className="card-head">
          <h3>Set up your first team</h3>
          <p className="xs subtle">
            EOS runs on teams — the leadership team, and one per department as it cascades.
          </p>
        </div>
        <div className="card-body">
          <p className="muted">
            Nothing is set up yet. Add a team on the Teams tab, put people on it from the employee
            directory, then schedule its first Level 10.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => onOpenTab('teams')}>
            Go to Teams
          </button>
        </div>
      </div>
    );
  }

  const { rocks: rockStats, scorecard, todos: todoStats, issues, lastMeeting, nextMeeting } = summary;
  const landed = rockStats.onTrack + rockStats.complete;
  const hitRate =
    scorecard.scored > 0 ? `${Math.round((scorecard.hit / scorecard.scored) * 100)}%` : '—';

  const today = eosToday();
  const openTodos = todos.filter((t) => !t.IsDone);
  /* Soonest first, and to-dos with no due date last: the panel is "due soon",
     so an undated one is not what it is answering. */
  const dueSoon = [...openTodos].sort((a, b) => (a.DueDate ?? '9999').localeCompare(b.DueDate ?? '9999'));

  return (
    <div className="eos">
      <div className="eos-stats">
        <button type="button" className="eos-stat" onClick={() => onOpenTab('rocks')}>
          <div className="label">Rocks on track</div>
          <div className={`value${rockStats.offTrack > 0 ? ' flag' : ''}`}>
            {landed}
            <span className="of"> / {rockStats.total}</span>
          </div>
          <div className="hint">
            {rockStats.total === 0
              ? 'none set this quarter'
              : rockStats.offTrack > 0
                ? `${rockStats.offTrack} off track`
                : 'View rocks'}
          </div>
        </button>

        <button type="button" className="eos-stat" onClick={() => onOpenTab('scorecard')}>
          <div className="label">Scorecard last week</div>
          <div className="value">{hitRate}</div>
          <div className="hint">
            {scorecard.measurables === 0
              ? 'no measurables yet'
              : `${scorecard.hit} of ${scorecard.scored} goals hit`}
          </div>
        </button>

        <button type="button" className="eos-stat" onClick={() => onOpenTab('todos')}>
          <div className="label">Open to-dos</div>
          <div className={`value${todoStats.overdue > 0 ? ' flag' : ''}`}>{todoStats.open}</div>
          <div className="hint">
            {todoStats.overdue > 0 ? (
              <span className="eos-overdue">{todoStats.overdue} overdue</span>
            ) : (
              'None overdue'
            )}
          </div>
        </button>

        <button type="button" className="eos-stat" onClick={() => onOpenTab('issues')}>
          <div className="label">Open issues</div>
          <div className="value">{issues.openShort}</div>
          <div className="hint">
            {issues.openLong > 0 ? `${issues.openLong} parked long term` : 'short-term list'}
          </div>
        </button>

        <div className="eos-stat">
          <div className="label">{nextMeeting ? 'Next meeting' : 'Last meeting'}</div>
          <div className="value sm">
            {nextMeeting
              ? formatDate(nextMeeting.date)
              : lastMeeting
                ? /* The bar for a Level 10 is an 8 — that is where the name comes
                     from — so the rating is the headline once there is one. */
                  lastMeeting.averageRating === null
                  ? 'Not rated'
                  : `Rated ${lastMeeting.averageRating.toFixed(1)}`
                : '—'}
          </div>
          <div className="hint">
            {nextMeeting ? (
              <Link to={`/eos/meetings/${nextMeeting.meetingId}`}>
                {nextMeeting.status === 'in_progress' ? 'Rejoin meeting' : 'Start meeting'}
              </Link>
            ) : lastMeeting ? (
              <Link to={`/eos/meetings/${lastMeeting.meetingId}`}>Recap</Link>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onOpenTab('meetings')}
              >
                Schedule one
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="eos-two-col">
        <section className="card">
          <div className="card-head">
            <h3>{summary.quarter} Rocks</h3>
          </div>
          <div className="card-body">
            <div className="eos-list">
              {rocks.map((rock) => (
                <div className="eos-row" key={rock.RockId}>
                  <span className={`eos-pill ${rockBand(rock.Status)}`}>
                    {ROCK_STATUS_LABELS[rock.Status]}
                  </span>
                  <div className="grow">
                    <div className="title">{rock.Title}</div>
                  </div>
                  <span className="meta">{rock.OwnerName ?? 'Unassigned'}</span>
                </div>
              ))}
              {rocks.length === 0 && <div className="muted">No rocks yet this quarter.</div>}
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h3>To-Dos due soon</h3>
          </div>
          <div className="card-body">
            <div className="eos-list">
              {dueSoon.slice(0, 8).map((todo) => (
                <div className="eos-row" key={todo.TodoId}>
                  <div className="grow">
                    <div className="title">{todo.Title}</div>
                  </div>
                  <span
                    className={`meta${todo.DueDate && todo.DueDate.slice(0, 10) < today ? ' eos-overdue' : ''}`}
                  >
                    {todo.DueDate ? formatDate(todo.DueDate) : 'no date'}
                  </span>
                  <span className="meta">{todo.OwnerName ?? 'Unassigned'}</span>
                </div>
              ))}
              {openTodos.length === 0 && <div className="muted">All clear.</div>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
